import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';

import Sidenote from 'components/sidenote/Sidenote';
import ActionButton from 'components/buttons/action-button/ActionButton';

import { __t, applyTranslations } from 'utils/language';
import { getMissingPaymentInfo, getSettingsUrl, isAccountActivated } from './utils/general';
import { styleOnlySanitizeGenericHtml } from 'utils/sanitize';

import './influencer-composed-message.scss';

applyTranslations(require('./influencer-composed-message.i18n.json'));

import type { ApprovalStatus } from 'types';

type OwnProps = {
  ignoreIfPaymentDetailsPresent?: boolean,
  showButton?: boolean,
  sidenoteClassName?: string
};

type StateProps = {
  approvalStatus: ApprovalStatus,
  settingsUrl: string | null,
  missingPaymentInfo: 'all' | 'bank' | 'business' | 'none',
  isEmailVerified: boolean,
  hasConnectedFacebook: boolean
};

type Props = OwnProps & StateProps;

const KEY = 'influencer_composed_message';

const InfluencerComposedMessage = ({
  approvalStatus,
  isEmailVerified,
  settingsUrl,
  missingPaymentInfo,
  showButton,
  ignoreIfPaymentDetailsPresent,
  hasConnectedFacebook,
  sidenoteClassName
}: Props): JSX.Element | null => {
  let showMessage = true;

  if ((hasConnectedFacebook && ignoreIfPaymentDetailsPresent && missingPaymentInfo === 'none')
    || (hasConnectedFacebook && missingPaymentInfo === 'none' && approvalStatus === 'approved' && isEmailVerified)
  ) {
    showMessage = false;
  }

  const approvalStatusMessage = approvalStatus === 'approved'
    ? null
    : __t(`${KEY}.approval_message.${approvalStatus}`);

  const paymentInfoMessage = approvalStatus !== 'rejected'
    ? __t(`${KEY}.bank_and_business_details.${missingPaymentInfo}.text.approval_${approvalStatus}`)
    : null;

  const shouldShowButton = showButton !== false && approvalStatus !== 'rejected';

  const buttonText = shouldShowButton
    ? __t(`${KEY}.bank_and_business_details.${missingPaymentInfo}.button_text`)
    : null;

  const sidenoteClasses = classnames('influencer-composed-message__sidenote', sidenoteClassName, {
    'influencer-composed-message__sidenote--sticky': true
  });

  const paymentAndStatusMessageClass = 'influencer-composed-message__payment-and-status-message';

  const paymentAndStatusMessageClasses = classnames(paymentAndStatusMessageClass, {
    [`${paymentAndStatusMessageClass}--without-button`]: showButton === false || !hasConnectedFacebook
  });

  if (((isEmpty(approvalStatusMessage) && isEmpty(paymentInfoMessage)) || !showMessage) && isEmailVerified) {
    return null;
  }

  return (
    <Sidenote
      withCenteredIcon
      type="info"
      className={sidenoteClasses}
      layout="horizontal"
    >
      <div className="influencer-composed-message">
        {
          !(isEmpty(approvalStatusMessage) && isEmpty(paymentInfoMessage)) && showMessage && (
            <div className="influencer-composed-message__payment-and-status">
              <div className={paymentAndStatusMessageClasses}>
                <span dangerouslySetInnerHTML={{ __html: styleOnlySanitizeGenericHtml(approvalStatusMessage ?? '') }} />
                {paymentInfoMessage}
              </div>
              {buttonText && hasConnectedFacebook && settingsUrl && (
              <ActionButton
                color="secondary"
                link={settingsUrl}
              >
                {buttonText}
              </ActionButton>
              )}
            </div>
          )
        }
        {!isEmailVerified && (
          <div className="influencer-composed-message__veryfication">
            <strong>
              {__t(`${KEY}.verification_message_text`)}
            </strong>
          </div>
        )}
      </div>
    </Sidenote>
  );
};

const mapStateToProps = ({
  account,
  influencerProfile,
  organizations,
  organizationSettings,
  facebookButton
}): StateProps => ({
  settingsUrl: getSettingsUrl(
    organizations?.currentOrganization?.id,
    organizations.mapByOrganizationId
  ),
  missingPaymentInfo: getMissingPaymentInfo(organizationSettings.organization),
  isEmailVerified: isAccountActivated(account?.currentAccount ?? null),
  approvalStatus: influencerProfile.profile.approvalStatus,
  hasConnectedFacebook: facebookButton.isFBLoginActive && !facebookButton.isFBLoginExpired
});

export default connect(mapStateToProps)(InfluencerComposedMessage);
